import { Link } from '@inertiajs/react';
import { m } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import AppLogoIcon from '@/components/app-logo-icon';
import { fadeUp, stagger } from '@/lib/landing-motion';

const UPTIME_URL =
    'https://uptime.khuirulhuda.my.id/report/uptime/052536156a3c559b14cd963feb25b4a0/';

const footerLinks = {
    'Akses Cepat': [
        { label: 'Presensi Santri', href: '/presensi' },
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Dokumentasi', href: '/bantuan' },
    ],
    Bantuan: [{ label: 'Panduan Sistem', href: '/bantuan' }],
    Legal: [{ label: 'Kebijakan Privasi', href: '#' }],
};

export default function LandingFooter({
    name,
    address,
    logo,
}: {
    name: string;
    address?: string;
    logo?: string;
}) {
    return (
        <footer className="border-t border-border/60 bg-muted/30 px-5 py-16 lg:px-8">
            <m.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                className="mx-auto max-w-6xl"
            >
                <div className="grid grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-5">
                    {/* Brand column */}
                    <m.div
                        variants={fadeUp}
                        className="col-span-2 space-y-5 lg:col-span-2"
                    >
                        <Link href="/" className="flex items-center gap-2.5">
                            <div className="flex size-9 items-center justify-center overflow-hidden rounded-xl bg-primary text-white">
                                {logo ? (
                                    <img
                                        src={logo}
                                        alt={name}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <AppLogoIcon className="size-5 text-white" />
                                )}
                            </div>
                            <span className="text-base font-semibold tracking-tight text-foreground">
                                {name}
                            </span>
                        </Link>

                        <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">
                            {address ||
                                'Layanan presensi digital terpadu untuk lingkungan pesantren yang disiplin, aman, dan modern.'}
                        </p>

                        {/* System status */}
                        <a
                            href={UPTIME_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-2.5 rounded-full border border-border/60 bg-background px-3.5 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <span className="relative flex size-2">
                                <m.span
                                    animate={{
                                        scale: [1, 2, 1],
                                        opacity: [0.7, 0, 0.7],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                    className="absolute inset-0 rounded-full bg-primary"
                                />
                                <span className="relative size-2 rounded-full bg-primary" />
                            </span>
                            All systems operational
                            <ExternalLink className="size-3 transition-transform group-hover:translate-x-0.5" />
                        </a>
                    </m.div>

                    {/* Link columns */}
                    {Object.entries(footerLinks).map(([section, links]) => (
                        <m.div
                            key={section}
                            variants={fadeUp}
                            className="space-y-4"
                        >
                            <h4 className="text-xs font-semibold tracking-wide text-foreground uppercase">
                                {section}
                            </h4>
                            <ul className="space-y-2.5">
                                {links.map((link) => (
                                    <li key={link.href}>
                                        <Link
                                            href={link.href}
                                            className="text-sm text-muted-foreground transition-colors hover:text-primary"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </m.div>
                    ))}
                </div>

                {/* Bottom bar */}
                <m.div
                    variants={fadeUp}
                    className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-border/60 pt-8 sm:flex-row"
                >
                    <p className="text-xs text-muted-foreground">
                        © {new Date().getFullYear()} {name}. Seluruh hak cipta
                        dilindungi.
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Powered by Sistem Presensi Jam Malam
                    </p>
                </m.div>
            </m.div>
        </footer>
    );
}
